import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaGraduationCap, FaBriefcase, FaClock, FaBookOpen, FaChartLine } from 'react-icons/fa';

export const FiliereTemplate = ({ filiere }) => {
  return (
    <div className="min-h-screen bg-white py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6 text-sm text-gray-600">
          <Link to="/" className="hover:text-brand-primary">Accueil</Link>
          {' '}/{' '}
          <Link to="/filieres" className="hover:text-brand-primary">Filières</Link>
          {' '}/{' '}
          <span className="text-brand-primary font-semibold">{filiere.name}</span>
        </div>

        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="text-5xl sm:text-6xl mb-4">{filiere.icon}</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-brand-secondary">{filiere.name}</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">{filiere.tagline}</p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
          <Card className="p-6 text-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 border-none">
            <FaClock className="text-3xl text-brand-primary mx-auto mb-3" />
            <h3 className="font-bold text-brand-secondary mb-1">Durée</h3>
            <p className="text-gray-700">{filiere.duree}</p>
          </Card>
          <Card className="p-6 text-center bg-gradient-to-br from-brand-secondary/10 to-brand-primary/10 border-none">
            <FaGraduationCap className="text-3xl text-brand-secondary mx-auto mb-3" />
            <h3 className="font-bold text-brand-secondary mb-1">Diplôme</h3>
            <p className="text-gray-700">{filiere.diplome}</p>
          </Card>
          <Card className="p-6 text-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 border-none">
            <FaBookOpen className="text-3xl text-brand-primary mx-auto mb-3" />
            <h3 className="font-bold text-brand-secondary mb-1">Mode</h3>
            <p className="text-gray-700">{filiere.mode}</p>
          </Card>
          <Card className="p-6 text-center bg-gradient-to-br from-brand-secondary/10 to-brand-primary/10 border-none">
            <FaChartLine className="text-3xl text-brand-secondary mx-auto mb-3" />
            <h3 className="font-bold text-brand-secondary mb-1">Débouchés</h3>
            <p className="text-gray-700">{filiere.nombreDebouches}+ métiers</p>
          </Card>
        </div>

        {/* Description */}
        <div className="mb-12 sm:mb-16">
          <Card className="p-8 sm:p-12 bg-gray-50 border-none">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-brand-primary">Présentation</h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">{filiere.description}</p>
            {filiere.objectifs && (
              <div className="mt-8">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-brand-secondary">Objectifs de la formation</h3>
                <ul className="space-y-3">
                  {filiere.objectifs.map((obj, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-brand-primary text-xl">•</span>
                      <span className="text-gray-700">{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>

        {/* Programme */}
        {filiere.programme && (
          <div className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-brand-secondary text-center">Programme d'Études</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {filiere.programme.map((semestre, index) => (
                <Card key={index} className="p-6 sm:p-8 border-l-4 border-brand-primary">
                  <h3 className="text-xl font-bold mb-4 text-brand-secondary">{semestre.titre}</h3>
                  <ul className="space-y-2">
                    {semestre.matieres.map((matiere, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-brand-primary mt-1">▸</span>
                        <span className="text-gray-700">{matiere}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Débouchés */}
        <div className="mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-brand-secondary text-center">Débouchés Professionnels</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filiere.debouches.map((debouche, index) => (
              <Card key={index} className="p-6 card-hover border-none shadow-md bg-white">
                <FaBriefcase className="text-2xl text-brand-primary mb-3" />
                <h3 className="font-semibold text-brand-secondary mb-2">{debouche.titre}</h3>
                <p className="text-sm text-gray-600">{debouche.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Compétences */}
        {filiere.competences && (
          <div className="mb-12 sm:mb-16">
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5 border-none">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-brand-secondary text-center">Compétences Développées</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {filiere.competences.map((comp, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-brand-primary text-2xl">✓</span>
                    <span className="text-gray-700">{comp}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-8 sm:p-12 rounded-lg shadow-xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Intéressé(e) par cette filière ?</h2>
          <p className="text-base sm:text-lg mb-6">Rejoignez-nous et commencez votre parcours académique d'excellence</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="https://scolarite-cefat.net/preinscription">
              <Button 
                data-testid="filiere-cta-preinscription"
                className="bg-white text-brand-primary hover:bg-brand-accent font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-md shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              >
                Je me préinscris
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                data-testid="filiere-cta-contact"
                className="bg-brand-secondary hover:bg-brand-secondary/90 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-md shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              >
                Demander plus d'infos
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
